import React from 'react';
import DeviceCompatibilityStatus from './DeviceCompatibilityStatus';

/**
 * ResumeGameModal - Modal for resuming a crashed game
 */
export const ResumeGameModal = ({ 
  isOpen, 
  onResume, 
  onDiscard, 
  saveTime,
  gameId 
}) => {
  if (!isOpen) return null;

  const formatSaveTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="text-center">
          <div className="text-6xl mb-4">🔄</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Resume Game?
          </h2>
          <p className="text-gray-600 mb-6">
            We found a saved game from <strong>{formatSaveTime(saveTime)}</strong>.
            Would you like to resume where you left off?
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2">Game ID: {gameId}</h3>
            <p className="text-blue-700 text-sm">
              Your progress is safely saved and ready to continue.
            </p>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={onResume}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
            >
              ✅ Resume Game
            </button>
            <button
              onClick={onDiscard}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
            >
              🗑️ Start New
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * LeaveConfirmationModal - Modal for confirming navigation away from game
 */
export const LeaveConfirmationModal = ({ 
  isOpen, 
  onConfirm, 
  onCancel 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Unsaved Changes
          </h2>
          <p className="text-gray-600 mb-6">
            You have unsaved changes in your game. If you leave now, your progress will be lost.
          </p>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 text-sm">
              <strong>Tip:</strong> Your game is automatically saved every few seconds. 
              You can safely leave if you've been playing for a while.
            </p>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={onCancel}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
            >
              🚫 Stay Here
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
            >
              🚪 Leave Anyway
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * SaveAndExitModal - Modal for saving before exiting game
 */
export const SaveAndExitModal = ({ 
  isOpen, 
  onSaveAndExit, 
  onExitWithoutSaving, 
  onCancel,
  saveStatus = 'idle',
  gameId 
}) => {
  if (!isOpen) return null;

  const isSaving = saveStatus === 'saving';
  const saveComplete = saveStatus === 'saved';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="text-center">
          <div className="text-6xl mb-4">💾</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Save & Exit Game?
          </h2>
          <p className="text-gray-600 mb-6">
            Would you like to save your current progress before exiting? This will preserve team scores, current question, and game state.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2">Game ID: {gameId}</h3>
            <p className="text-blue-700 text-sm">
              {isSaving && '⏳ Saving your progress...'}
              {saveComplete && '✅ Progress saved successfully!'}
              {!isSaving && !saveComplete && '💾 Ready to save current game state'}
            </p>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-4 rounded-lg transition duration-200"
              disabled={isSaving}
            >
              ✋ Cancel
            </button>
            <button
              onClick={onExitWithoutSaving}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-lg transition duration-200"
              disabled={isSaving}
            >
              🚪 Exit Without Saving
            </button>
            <button
              onClick={onSaveAndExit}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200"
              disabled={isSaving}
            >
              {isSaving ? '⏳ Saving...' : (saveComplete ? '✅ Exit' : '💾 Save & Exit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * StatusBanner - Banner showing save status and controls
 */
export const StatusBanner = ({ 
  saveStatus, 
  lastSaveTime, 
  isFullscreen, 
  onToggleFullscreen,
  hasUnsavedChanges,
  gameId,
  onExitGame 
}) => {
  const getStatusIcon = () => {
    switch (saveStatus) {
      case 'saving': return '⏳';
      case 'saved': return '✅';
      case 'error': return '❌';
      default: return '💾';
    }
  };

  const getStatusText = () => {
    switch (saveStatus) {
      case 'saving': return 'Saving...';
      case 'saved': return 'Saved';
      case 'error': return 'Save Error';
      default: return 'Auto-save enabled';
    }
  };

  const getStatusColor = () => {
    switch (saveStatus) {
      case 'saving': return 'text-blue-600';
      case 'saved': return 'text-green-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const formatLastSave = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ago`;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-800 bg-opacity-90 backdrop-blur-sm border-t border-gray-600 px-3 py-1 z-60">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center space-x-3">
          {/* Save Status - Compact */}
          <div className="flex items-center space-x-1">
            <span className="text-sm">{getStatusIcon()}</span>
            <span className={`text-xs ${getStatusColor()}`}>
              {getStatusText()}
            </span>
          </div>

          {/* Last Save Time - Compact */}
          <div className="text-xs text-gray-300">
            {formatLastSave(lastSaveTime)}
          </div>

          {/* Unsaved Changes Indicator - Compact */}
          {hasUnsavedChanges && (
            <div className="flex items-center space-x-1 text-orange-400">
              <span className="text-xs">●</span>
              <span className="text-xs">Unsaved</span>
            </div>
          )}
        </div>

        {/* Controls - Compact */}
        <div className="flex items-center space-x-2">
          {/* Device Compatibility Status */}
          <DeviceCompatibilityStatus gameId={gameId} />

          {/* Exit Game Button */}
          {onExitGame && (
            <button
              onClick={onExitGame}
              className="px-2 py-1 text-xs bg-red-700 hover:bg-red-600 text-red-200 rounded transition duration-200"
              title="Save & Exit Game"
            >
              🚪 Exit
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
